import React, { useState } from 'react';
import { FiPlus, FiEdit2, FiTrash2, FiCheck, FiX, FiCreditCard, FiDollarSign, FiInfo, FiStar, FiBriefcase } from 'react-icons/fi';
import { useProfile } from '../../context/ProfileContext';

const BankAccounts = () => {
  const {
    bankAccounts,
    primaryAccountId,
    addBankAccount,
    removeBankAccount,
    updateBankAccount,
    setPrimaryAccount
  } = useProfile();

  const [showAddForm, setShowAddForm] = useState(false);
  const [editingAccount, setEditingAccount] = useState(null);
  const [formData, setFormData] = useState({
    bankName: '',
    branchName: '',
    accountHolderName: '',
    accountNumber: '',
    confirmAccountNumber: '',
    ifscCode: '',
    accountType: 'savings',
    swiftCode: '',
    branchAddress: '',
    upiId: '',
    isInternational: false,
    currency: 'INR'
  });

  const [errors, setErrors] = useState({});

  const bankOptions = [
    'State Bank of India',
    'HDFC Bank',
    'ICICI Bank',
    'Axis Bank',
    'Kotak Mahindra Bank',
    'Punjab National Bank',
    'Bank of Baroda',
    'Canara Bank',
    'Union Bank of India',
    'IDBI Bank',
    'Bank of India',
    'Central Bank of India',
    'Indian Bank',
    'Indian Overseas Bank',
    'UCO Bank',
    'Other'
  ];

  const resetForm = () => {
    setFormData({
      bankName: '',
      branchName: '',
      accountHolderName: '',
      accountNumber: '',
      confirmAccountNumber: '',
      ifscCode: '',
      accountType: 'savings',
      swiftCode: '',
      branchAddress: '',
      upiId: '',
      isInternational: false,
      currency: 'INR'
    });
    setErrors({});
    setShowAddForm(false);
    setEditingAccount(null);
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.bankName) newErrors.bankName = 'Bank name is required';
    if (!formData.accountHolderName) newErrors.accountHolderName = 'Account holder name is required';
    if (!formData.accountNumber) newErrors.accountNumber = 'Account number is required';
    if (formData.accountNumber !== formData.confirmAccountNumber) {
      newErrors.confirmAccountNumber = 'Account numbers do not match';
    }
    if (!formData.ifscCode) newErrors.ifscCode = 'IFSC code is required';
    if (formData.ifscCode && !/^[A-Z]{4}0[A-Z0-9]{6}$/.test(formData.ifscCode)) {
      newErrors.ifscCode = 'Invalid IFSC code format';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    if (editingAccount) {
      updateBankAccount(editingAccount, formData);
    } else {
      addBankAccount(formData);
    }

    resetForm();
  };

  const handleEdit = (account) => {
    setFormData({
      ...account,
      confirmAccountNumber: account.accountNumber
    });
    setEditingAccount(account.id);
    setShowAddForm(true);
  };

  const handleDelete = (accountId) => {
    if (window.confirm('Are you sure you want to remove this bank account?')) {
      removeBankAccount(accountId);
    }
  };

  const maskAccountNumber = (number) => {
    if (!number) return '';
    const length = number.length;
    if (length <= 4) return number;
    return '●●●●' + number.slice(-4);
  };

  return (
    <div className="container-fluid p-0">
      {/* Header */}
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h4 className="fw-bold mb-1">Bank Accounts</h4>
          <p className="text-muted mb-0">Manage your payment and settlement accounts</p>
        </div>
        <button
          className="btn btn-primary rounded-pill px-4"
          onClick={() => setShowAddForm(true)}
        >
          <FiPlus className="me-2" />
          Add Bank Account
        </button>
      </div>

      {/* Bank Accounts List */}
      {bankAccounts.length === 0 && !showAddForm ? (
        <div className="glass-card p-5 text-center">
          <FiBriefcase size={50} className="text-muted mb-3" />
          <h5 className="mb-2">No Bank Accounts Added</h5>
          <p className="text-muted mb-4">Add your bank account details for payments and settlements</p>
          <button
            className="btn btn-primary rounded-pill px-4"
            onClick={() => setShowAddForm(true)}
          >
            <FiPlus className="me-2" />
            Add Your First Account
          </button>
        </div>
      ) : (
        <div className="row g-4">
          {bankAccounts.map((account) => (
            <div key={account.id} className="col-lg-6">
              <div className={`glass-card p-4 ${account.id === primaryAccountId ? 'border-primary' : ''}`}>
                <div className="d-flex justify-content-between align-items-start mb-3">
                  <div className="d-flex align-items-center gap-3">
                    <div
                      className="rounded-circle bg-gradient d-flex align-items-center justify-content-center"
                      style={{
                        width: '50px',
                        height: '50px',
                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
                      }}
                    >
                      <FiCreditCard size={24} className="text-white" />
                    </div>
                    <div>
                      <h5 className="mb-1 fw-bold">{account.bankName}</h5>
                      <p className="text-muted mb-0 small">{account.branchName}</p>
                    </div>
                  </div>

                  {account.id === primaryAccountId && (
                    <span className="badge bg-primary bg-opacity-10 text-primary rounded-pill px-3">
                      <FiStar className="me-1" size={14} />
                      Primary
                    </span>
                  )}
                </div>

                <div className="account-details">
                  <div className="row g-3 mb-3">
                    <div className="col-6">
                      <small className="text-muted d-block">Account Holder</small>
                      <span className="fw-semibold">{account.accountHolderName}</span>
                    </div>
                    <div className="col-6">
                      <small className="text-muted d-block">Account Number</small>
                      <span className="fw-semibold font-monospace">
                        {maskAccountNumber(account.accountNumber)}
                      </span>
                    </div>
                    <div className="col-6">
                      <small className="text-muted d-block">IFSC Code</small>
                      <span className="fw-semibold font-monospace">{account.ifscCode}</span>
                    </div>
                    <div className="col-6">
                      <small className="text-muted d-block">Account Type</small>
                      <span className="fw-semibold text-capitalize">{account.accountType}</span>
                    </div>
                  </div>

                  {account.upiId && (
                    <div className="mb-3">
                      <small className="text-muted d-block">UPI ID</small>
                      <span className="fw-semibold">{account.upiId}</span>
                    </div>
                  )}

                  <div className="d-flex justify-content-between align-items-center pt-3 border-top">
                    <div className="d-flex gap-2">
                      {account.verified ? (
                        <span className="badge bg-success bg-opacity-10 text-success rounded-pill px-3">
                          <FiCheck className="me-1" />
                          Verified
                        </span>
                      ) : (
                        <span className="badge bg-warning bg-opacity-10 text-warning rounded-pill px-3">
                          Pending Verification
                        </span>
                      )}
                    </div>

                    <div className="d-flex gap-2">
                      {account.id !== primaryAccountId && (
                        <button
                          className="btn btn-sm btn-outline-primary rounded-pill px-3"
                          onClick={() => setPrimaryAccount(account.id)}
                        >
                          Set Primary
                        </button>
                      )}
                      <button
                        className="btn btn-sm btn-outline-secondary rounded-pill px-3"
                        onClick={() => handleEdit(account)}
                      >
                        <FiEdit2 size={14} />
                      </button>
                      <button
                        className="btn btn-sm btn-outline-danger rounded-pill px-3"
                        onClick={() => handleDelete(account.id)}
                      >
                        <FiTrash2 size={14} />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Add/Edit Bank Account Form */}
      {showAddForm && (
        <div className="glass-card p-4 mt-4">
          <div className="d-flex justify-content-between align-items-center mb-4">
            <h5 className="fw-bold mb-0">
              {editingAccount ? 'Edit Bank Account' : 'Add New Bank Account'}
            </h5>
            <button
              className="btn btn-outline-secondary btn-sm rounded-pill px-3"
              onClick={resetForm}
            >
              <FiX className="me-1" />
              Cancel
            </button>
          </div>

          <form onSubmit={handleSubmit}>
            <div className="row g-3">
              {/* Bank Name */}
              <div className="col-md-6">
                <label className="form-label">
                  Bank Name <span className="text-danger">*</span>
                </label>
                <select
                  className={`form-select ${errors.bankName ? 'is-invalid' : ''}`}
                  value={formData.bankName}
                  onChange={(e) => setFormData({ ...formData, bankName: e.target.value })}
                >
                  <option value="">Select Bank</option>
                  {bankOptions.map(bank => (
                    <option key={bank} value={bank}>{bank}</option>
                  ))}
                </select>
                {errors.bankName && (
                  <div className="invalid-feedback">{errors.bankName}</div>
                )}
              </div>

              {/* Branch Name */}
              <div className="col-md-6">
                <label className="form-label">Branch Name</label>
                <input
                  type="text"
                  className="form-control"
                  value={formData.branchName}
                  onChange={(e) => setFormData({ ...formData, branchName: e.target.value })}
                  placeholder="Enter branch name"
                />
              </div>

              {/* Account Holder Name */}
              <div className="col-md-6">
                <label className="form-label">
                  Account Holder Name <span className="text-danger">*</span>
                </label>
                <input
                  type="text"
                  className={`form-control ${errors.accountHolderName ? 'is-invalid' : ''}`}
                  value={formData.accountHolderName}
                  onChange={(e) => setFormData({ ...formData, accountHolderName: e.target.value })}
                  placeholder="As per bank records"
                />
                {errors.accountHolderName && (
                  <div className="invalid-feedback">{errors.accountHolderName}</div>
                )}
              </div>

              {/* Account Type */}
              <div className="col-md-6">
                <label className="form-label">Account Type</label>
                <select
                  className="form-select"
                  value={formData.accountType}
                  onChange={(e) => setFormData({ ...formData, accountType: e.target.value })}
                >
                  <option value="savings">Savings</option>
                  <option value="current">Current</option>
                  <option value="salary">Salary</option>
                  <option value="nri">NRI</option>
                </select>
              </div>

              {/* Account Number */}
              <div className="col-md-6">
                <label className="form-label">
                  Account Number <span className="text-danger">*</span>
                </label>
                <input
                  type="text"
                  className={`form-control ${errors.accountNumber ? 'is-invalid' : ''}`}
                  value={formData.accountNumber}
                  onChange={(e) => setFormData({ ...formData, accountNumber: e.target.value })}
                  placeholder="Enter account number"
                />
                {errors.accountNumber && (
                  <div className="invalid-feedback">{errors.accountNumber}</div>
                )}
              </div>

              {/* Confirm Account Number */}
              <div className="col-md-6">
                <label className="form-label">
                  Confirm Account Number <span className="text-danger">*</span>
                </label>
                <input
                  type="text"
                  className={`form-control ${errors.confirmAccountNumber ? 'is-invalid' : ''}`}
                  value={formData.confirmAccountNumber}
                  onChange={(e) => setFormData({ ...formData, confirmAccountNumber: e.target.value })}
                  placeholder="Re-enter account number"
                />
                {errors.confirmAccountNumber && (
                  <div className="invalid-feedback">{errors.confirmAccountNumber}</div>
                )}
              </div>

              {/* IFSC Code */}
              <div className="col-md-6">
                <label className="form-label">
                  IFSC Code <span className="text-danger">*</span>
                </label>
                <input
                  type="text"
                  className={`form-control ${errors.ifscCode ? 'is-invalid' : ''}`}
                  value={formData.ifscCode}
                  onChange={(e) => setFormData({ ...formData, ifscCode: e.target.value.toUpperCase() })}
                  placeholder="E.g., SBIN0001234"
                  maxLength="11"
                />
                {errors.ifscCode && (
                  <div className="invalid-feedback">{errors.ifscCode}</div>
                )}
                <small className="text-muted">11-character code (4 letters + 0 + 6 characters)</small>
              </div>

              {/* UPI ID */}
              <div className="col-md-6">
                <label className="form-label">UPI ID (Optional)</label>
                <input
                  type="text"
                  className="form-control"
                  value={formData.upiId}
                  onChange={(e) => setFormData({ ...formData, upiId: e.target.value })}
                  placeholder="yourname@upi"
                />
              </div>

              {/* International Account Options */}
              <div className="col-12">
                <div className="form-check mb-3">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id="internationalAccount"
                    checked={formData.isInternational}
                    onChange={(e) => setFormData({ ...formData, isInternational: e.target.checked })}
                  />
                  <label className="form-check-label" htmlFor="internationalAccount">
                    International Account
                  </label>
                </div>
              </div>

              {formData.isInternational && (
                <>
                  {/* SWIFT Code */}
                  <div className="col-md-6">
                    <label className="form-label">SWIFT/BIC Code</label>
                    <input
                      type="text"
                      className="form-control"
                      value={formData.swiftCode}
                      onChange={(e) => setFormData({ ...formData, swiftCode: e.target.value.toUpperCase() })}
                      placeholder="E.g., SBININBB123"
                    />
                  </div>

                  {/* Currency */}
                  <div className="col-md-6">
                    <label className="form-label">Currency</label>
                    <select
                      className="form-select"
                      value={formData.currency}
                      onChange={(e) => setFormData({ ...formData, currency: e.target.value })}
                    >
                      <option value="INR">INR - Indian Rupee</option>
                      <option value="USD">USD - US Dollar</option>
                      <option value="EUR">EUR - Euro</option>
                      <option value="GBP">GBP - British Pound</option>
                      <option value="AED">AED - UAE Dirham</option>
                      <option value="SGD">SGD - Singapore Dollar</option>
                    </select>
                  </div>
                </>
              )}

              {/* Branch Address */}
              <div className="col-12">
                <label className="form-label">Branch Address (Optional)</label>
                <textarea
                  className="form-control"
                  rows="2"
                  value={formData.branchAddress}
                  onChange={(e) => setFormData({ ...formData, branchAddress: e.target.value })}
                  placeholder="Enter complete branch address"
                />
              </div>
            </div>

            {/* Form Actions */}
            <div className="d-flex justify-content-end gap-3 mt-4">
              <button
                type="button"
                className="btn btn-outline-secondary rounded-pill px-4"
                onClick={resetForm}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="btn btn-primary rounded-pill px-4"
              >
                {editingAccount ? 'Update Account' : 'Add Account'}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Information Notice */}
      <div className="alert alert-info d-flex align-items-center mt-4">
        <FiInfo className="me-2" />
        <div>
          <strong>Important:</strong> Bank account details are encrypted and stored securely.
          Verification may take 24-48 hours. You can add multiple accounts but only one can be set as primary for settlements.
        </div>
      </div>
    </div>
  );
};

export default BankAccounts;